import React from 'react';
import {
  NavigationItem as SiteNavigationItem,
  SiteComponent,
  SiteComponentConfigProps,
  SiteComponentPlugin,
  SiteComponentProps,
  SlotRenderer,
  useSiteContext,
  useSubscribeComponentChanges
} from '@sierralabs/site-ui';

import ProtectedRoute from './common/ProtectedRoute';
import { navigationList } from './environments/navigation';
import AuthGuard from './guards/auth.guard';
import { NavigationItem } from './secure-portal/NavigationList';
import SecurePortal from './secure-portal/SecurePortal';

export interface QMSPortalProps {}

export const QMSPortal: React.FC<
  SiteComponentProps<QMSPortalProps>
> = props => {
  const component = useSubscribeComponentChanges(QMSPortalPlugin, props);
  const { isHover } = useSiteContext();
  const classes = ['secure-portal'];
  if (isHover(component)) {
    classes.push('component-hover');
  }
  return (
    <SiteComponent plugin={QMSPortalPlugin} {...props}>
      <ProtectedRoute
        // always allow access to SecurePortal if a User is logged in (AuthGuard)
        guards={[AuthGuard]}
      >
        <SecurePortal
          navigation={<SlotRenderer component={component} slot='navigation' />}
        >
          <SlotRenderer component={component} slot='routes' />
        </SecurePortal>
      </ProtectedRoute>
    </SiteComponent>
  );
};

export const QMSPortalConfigurator: React.FC<
  SiteComponentConfigProps<QMSPortalProps>
> = () => <></>;

interface FlatNavigationItem
  extends SiteNavigationItem,
    Pick<NavigationItem, 'roles'> {}

export function flattenNavigation(
  navigation: NavigationItem[]
): FlatNavigationItem[] {
  return navigation.reduce((all: SiteNavigationItem[], item) => {
    const newItem = {
      ...item,
      path: item.path || '',
      label: item.label || '',
      items: item.items && flattenNavigation(item.items)
    };
    delete newItem.iconEl;
    all.push(newItem);
    if (newItem.items) {
      all.push(...newItem.items);
      delete newItem.items;
    }
    return all;
  }, []);
}

export const QMSPortalPlugin: SiteComponentPlugin<QMSPortalProps> = {
  name: 'QMS Portal',
  type: 'qms-portal',
  component: QMSPortal,
  configurator: QMSPortalConfigurator,
  slots: [
    {
      name: 'Navigation',
      type: 'site-navigation',
      slot: 'navigation',
      props: { navigation: flattenNavigation(navigationList) }
    },
    {
      name: 'Routes',
      type: 'component-stack',
      slot: 'routes'
    }
  ]
};
